import type { MessageDescriptor } from '@tov-plus/domain';
import { PlatformLanguageCode } from '@tov-plus/domain';
import { componentCatalogs, msgForComponent } from './catalogs';
import { getLocale } from './i18n';

/** Render only explicitly described system copy. Stored/custom text stays literal. */
export function getGeneratedMessage(
  source: string,
  descriptor: MessageDescriptor | undefined,
): string {
  return renderGeneratedMessage(source, descriptor, 0);
}

function renderGeneratedMessage(
  source: string,
  descriptor: MessageDescriptor | undefined,
  depth: number,
): string {
  if (
    depth > 6 ||
    !descriptor ||
    typeof descriptor.key !== 'string' ||
    descriptor.parts !== undefined ||
    !['tov.generated.', 'tov.api.', 'general.invariants.'].some((prefix) =>
      descriptor.key.startsWith(prefix),
    )
  )
    return source;
  if (!Object.hasOwn(componentCatalogs.common.en, descriptor.key))
    return source;
  const english = componentCatalogs.common.en[descriptor.key];
  if (!english) return source;
  const values = descriptor.values ?? {};
  if (typeof values !== 'object' || Array.isArray(values)) return source;
  const placeholders = [...english.matchAll(/\{(\w+)\}/g)];
  if (
    placeholders.some(
      ([, key]) =>
        !Object.hasOwn(values, key!) ||
        (typeof values[key!] !== 'string' &&
          typeof values[key!] !== 'number') ||
        (typeof values[key!] === 'number' && !Number.isFinite(values[key!])),
    )
  )
    return source;
  const interpolate = (text: string) =>
    text.replace(/\{(\w+)\}/g, (placeholder, key: string) =>
      Object.hasOwn(values, key) ? String(values[key]) : placeholder,
    );
  if (
    interpolate(
      msgForComponent(descriptor.key, PlatformLanguageCode.English, 'common'),
    ) !== source
  )
    return source;
  const valueMessages = descriptor.value_messages;
  if (
    valueMessages !== undefined &&
    (!valueMessages ||
      typeof valueMessages !== 'object' ||
      Array.isArray(valueMessages))
  )
    return source;
  return msgForComponent(descriptor.key, getLocale(), 'common').replace(
    /\{(\w+)\}/g,
    (placeholder, key: string) => {
      if (!Object.hasOwn(values, key)) return placeholder;
      const value = String(values[key]);
      return valueMessages && Object.hasOwn(valueMessages, key)
        ? renderGeneratedMessage(value, valueMessages[key], depth + 1)
        : value;
    },
  );
}
